import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Close as CloseIcon } from '@material-ui/icons';
import { isEmpty } from 'lodash';

import { labelSizes, bacnetUnits } from '../../constants';
import OnOff from '../Controls/OnOff';
import Toggle from '../Controls/Toggle';
import ListControl from '../Controls/List';
import InformationText from '../Controls/InformationText';
import NumericValue from '../Controls/NumericValue';
import useStyles from './styles';

const getDummyText = (dataPoint) => {
  const {config = {}} = dataPoint;

  const {customEnum,supportedControls} = config;

  if(Array.isArray(customEnum) && customEnum.length > 0){
    return customEnum[0];
  }

  if(customEnum){
    return Object.values(customEnum)[0];
  }

  if(supportedControls.includes('numericValue')){
    return Math.floor(Math.random() * (999 - 10 + 1)) + 10;
  }

  return 'Text';

};

const DraggableDataPoint = ({
  dataPoint,
  isSelected,
  onDelete,
  mode,
  onChangeControlValue,
  controlValue,
}) => {
  const classes = useStyles();

  const isInEditor = mode === 'edit';

  const isControlDisabled = isInEditor || (!isInEditor && dataPoint?.config?.readOnly);

  const dummyText = useMemo(() => getDummyText(dataPoint),[dataPoint]);

  const symbol = bacnetUnits[dataPoint?.config?.userUnits || dataPoint?.config?.units]?.symbol || '';

  const size = labelSizes[dataPoint.labelSize];
  const color = dataPoint.labelColorConfig;


  const triggerChange = useCallback((newVal) => {
    !isControlDisabled && onChangeControlValue?.(newVal);
  }, [isControlDisabled, onChangeControlValue]);

  /**
   * Generates list options from dataPoint's custom enum configuration.
   * Filters out entries where customEnumVisibility is false.
   */
  const listOptions = useMemo(() => {
    const options = [];
    const map = {};

    if (!isEmpty(dataPoint?.config?.customEnum)) {
      const {customEnum,customEnumVisibility} = dataPoint.config;

      Object.keys(customEnum).forEach((enumKey) => {
        if(customEnumVisibility && customEnumVisibility[enumKey] === false){
          return null;
        }
        const option = {
          value: enumKey,
          name: customEnum[enumKey],
        };
        options.push(option);
        map[enumKey] = option;
      });

      // Ensure the current controlValue is included in options (as disabled) even if not in customEnumVisibility
      if (map[controlValue] === undefined) {
        options.unshift({
          value: controlValue,
          name: customEnum[controlValue],
          disabled: true,
        });
      }
    }

    return options;
  }, [dataPoint]);

  /**
   * Gets the computed list value based on editor state and control value.
   * @returns {string|number|null} The control value if valid and not in editor, first list option value, or null
   */
  const getComputedListValue = () => {
    if (isInEditor) {
      return null;
    }
    return controlValue;
  };

  const renderControlType = (controlType) => {
    const formatValue = () => {
      if (isInEditor) {
        return dummyText;
      }
      const numValue = Number(controlValue);
      //Check if the value is a key for a custom enum and return the corresponding value.
      if (dataPoint?.config?.customEnum?.[numValue]) {
        return dataPoint?.config?.customEnum[numValue];
      }
      if (isNaN(numValue)) {
        return controlValue;
      }
      return Number.isInteger(numValue)
        ? String(numValue)
        : numValue.toFixed(1);
    };

    if (controlType === 'informationText') {

      const value = formatValue();

      return (
        <InformationText
          value={value}
          symbol={symbol}
          isDisabled={isControlDisabled}
          style={{
            fontSize: size,
            color: dataPoint.labelColorConfig,
          }}
        />
      );
    }

    if (controlType === 'toggle') {
      return (
        <Toggle
          onChange={triggerChange}
          value={isInEditor ? true : controlValue}
          isDisabled={isControlDisabled}
        />
      );
    }

    if (controlType === 'onOff') {
      const textValue = formatValue();
      return (
        <OnOff
          onChange={triggerChange}
          value={isInEditor ? true : controlValue}
          textValue={textValue}
          isDisabled={isControlDisabled}
        />
      );
    }

    if (controlType === 'list') {

      return (
        <ListControl
          mode={mode}
          onChange={triggerChange}
          value={getComputedListValue()}
          isDisabled={isControlDisabled}
          options={listOptions}
          textSize={size}
        />
      );
    }

    if (controlType === 'numericValue') {
      return (
        <NumericValue
          value={isInEditor ? dummyText : controlValue}
          max={Number(dataPoint?.config?.customEnum?.max)}
          min={Number(dataPoint?.config?.customEnum?.min)}
          step={Number(dataPoint?.config?.customEnum?.step) || 1}
          onChange={triggerChange}
          isDisabled={isControlDisabled}
          symbol={symbol}
          textSize={size}
          textColor={color}
        />
      );
    }

    return renderControlType('informationText');
  };

  return (
    <div
      className={clsx(classes.draggableDataPoint, {
        [classes.labelTop]: dataPoint.labelPosition === 'top',
        [classes.labelBottom]: dataPoint.labelPosition === 'bottom',
        [classes.labelLeft]: dataPoint.labelPosition === 'left',
        [classes.labelRight]: dataPoint.labelPosition === 'right',
        isSelected: isSelected,
        moveable:isInEditor,
        inControlTab: !isInEditor,
      })}
      style={{
        backgroundColor: dataPoint.backgroundColorConfig ?? 'white',
      }}
    >
      <div
        className={classes.draggableDataPoint__label}
        style={{
          fontSize: size,
          color: dataPoint.labelColorConfig,
        }}
      >
        {dataPoint.label}
      </div>
      <div className={classes.draggableDataPoint__control}>
        {renderControlType(dataPoint?.controlType)}
      </div>
      {isInEditor && isSelected && (
        <div
          className={classes.draggableDataPoint__CloseBtn}
          onClick={onDelete}
        >
          <CloseIcon />
        </div>
      )}
    </div>
  );
};

export default DraggableDataPoint;
